import { notFoundPage } from "../../utils/notFound";
import { GetServerSidePropsContext, GetServerSidePropsResult } from "next";
import { NextSeo } from "next-seo";
import { toast } from "react-hot-toast";
import axios from "axios";
import { getIdToken } from "../../utils/firebaseAuth";
import { useRouter } from "next/router";
import {
  ConversionPage as ConversionPageComponent,
  ConversionPageProps,
} from "@jammable/ui-conversion-feature";
import { useModal } from "../../context/ModalContext";
import DeleteConversionModal from "../../components/modals/DeleteConversionModal";
import { useAuth } from "../../context/AuthProvider";
import { useEffect, useRef, useState } from "react";
import ReportModal from "@/components/modals/ReportModal";
import connectToDb from "@/utils/mongodb";
import { orderModelsByIds } from "@/utils/orderModels";
import useAuthModal from "@/hooks/useAuthModal";
import DiscordCta from "../../components/DiscordCta";
import LockedCover from "@/components/studio/LockedCover";
import { lockedExpiresAt } from "@/utils/lockedCover";
import dynamic from "next/dynamic";
import { localePath } from "@/utils/localeSeo";

const UpgradeModal = dynamic(() => import("@/components/modals/UpgradeModal"), {
  ssr: false,
});

export default function ConversionPage({
  conversion,
  isUK,
}: ConversionPageProps & { isUK?: boolean }) {
  const { openModal } = useModal();
  const { user } = useAuth();
  const { checkAuthAndExecute } = useAuthModal();
  const router = useRouter();

  // Drive /advance polling for conversions that are still waiting on their
  // AI-generated source audio. Once the backend transitions the conversion to
  // `pending` (RunPod job kicked off), we reload the page so the standard
  // ConversionPageComponent picks up the new state.
  const advanceRunning = useRef(false);
  useEffect(() => {
    if (conversion?.status !== "pending_source") return;
    if (advanceRunning.current) return;
    advanceRunning.current = true;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      try {
        const token = await getIdToken();
        if (!token) {
          timer = setTimeout(tick, 4000);
          return;
        }
        const id = conversion.shortid || conversion._id;
        const { data } = await axios.post(
          `/api/v2/conversion/advance/${id}`,
          {},
          { headers: { Authorization: token } },
        );
        if (cancelled) return;
        if (data?.status === "pending_source") {
          timer = setTimeout(tick, 4000);
          return;
        }
        // Anything else (`pending`, `processing`, `failed`, …) means the
        // backend has moved past the generation phase — let SSR re-fetch.
        router.replace(router.asPath);
      } catch (err: any) {
        console.warn("advance poll failed", err?.response?.data || err);
        if (!cancelled) timer = setTimeout(tick, 6000);
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversion?.status, conversion?.shortid, conversion?._id]);

  let artistNames = conversion?.model?.artistname;
  if (conversion?.models && conversion.models.length > 0) {
    const names = conversion.models.map((model) => model.artistname);
    if (names.length > 1) {
      artistNames =
        names.slice(0, -1).join(", ") + " & " + names[names.length - 1];
    } else {
      artistNames = names[0];
    }
  }
  const title = `${
    conversion?.name
      ? `${conversion?.name} - AI Cover`
      : `AI ${artistNames} Cover #${conversion?.shortid}`
  } | Jammable`;
  const description = `Create AI ${artistNames} covers as seen on TikTok and YouTube in seconds! Jammable has thousands of community uploaded AI voices available for creative use now!`;

  const [, setIsVideoModalOpened] = useState(false);

  const actionHandler = async (actionKey: string) => {
    switch (actionKey) {
      case "delete":
        openModal(
          "delete-conversion-modal",
          <DeleteConversionModal conversion={conversion} />,
        );
        break;

      case "report":
        if (!checkAuthAndExecute()) return false;
        openModal(
          "report-modal",
          <ReportModal type="conversion" item={conversion} />,
        );
        break;
      case "createVideo":
        setIsVideoModalOpened(true);
        // openModal(
        //   "create-video-modal",
        //   <CreateVideoModal2 conversion={conversionData} />,
        // );
        break;
      case "manageSubscription": {
        const token = await getIdToken();
        axios
          .post(`/api/payment/manageSubscription`, undefined, {
            headers: { Authorization: token },
          })
          .then((res) => (window.location.href = res.data.url))
          .catch(() =>
            toast.error("Something went wrong, please contact support!"),
          );
        break;
      }
      case "redirectToSettings": {
        window.location.href = "/settings";
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      <NextSeo
        title={title}
        description={description}
        canonical={`https://www.jammable.com/conversion/${conversion?.shortid || conversion?._id}`}
        // A cover page is one user's output, not a landing page: ~300-500
        // characters of text, titled with whatever they named the upload
        // (file names, TTS text, copyrighted song titles), and it is served
        // as 200 whether the cover succeeded, failed, is pending or is locked.
        // Shared links still work and still get a preview card; search engines
        // just should not list them. Links on it (voice, profile) still count.
        noindex
        openGraph={{
          url: `https://www.jammable.com/conversion/${conversion?.shortid || conversion?._id}`,
          title: title,
          description: description,
          images: [
            {
              url: conversion?.model?.image?.url,
              width: 900,
              height: 900,
              alt: "Jammable Model",
            },
          ],
        }}
      />

      <div className="tw-mx-auto tw-w-full tw-max-w-[1280px] tw-px-4 tw-pt-2 md:tw-px-8">
        {conversion?.status === "locked" ||
        conversion?.status === "unlocking" ? (
          <LockedCover
            conversion={conversion}
            isOwner={Boolean(
              user?._id &&
                (conversion as any)?.user?._id &&
                String(user._id) === String((conversion as any).user._id),
            )}
            // Credits count too: anyone who could make a cover right now
            // gets "Make it now" instead of the paywall.
            hasPlan={!!user?.quota?.conversions?.hasCredits}
            onPaywall={(trigger) => {
              // The paywall's "leave without your cover?" step: ModalContext
              // asks the guard before closing (see UpgradeModal exitGuard).
              const exitGuard: { attempt?: () => boolean } = {};
              openModal(
                "upgrade-modal",
                <UpgradeModal
                  from="locked-cover"
                  dailyLimit={false}
                  voices={conversion?.models}
                  coverName={(conversion as any)?.name}
                  heldUntil={lockedExpiresAt((conversion as any)?.createdAt)}
                  exitGuard={exitGuard}
                  conversionId={conversion?.shortid}
                  trigger={trigger}
                />,
                {
                  className: "tw-p-0",
                  onBeforeClose: () =>
                    exitGuard.attempt ? exitGuard.attempt() : true,
                },
              );
            }}
          />
        ) : (
          <ConversionPageComponent
            conversion={conversion}
            actionHandler={actionHandler}
            signedInUser={user}
            isUK={isUK}
          />
        )}

        {/* Shared covers bring in people who have never seen the site.
            Placed below the player so it never competes with playback or
            the download action. */}
        <div className="tw-mx-auto tw-mb-8 tw-max-w-[1040px]">
          <DiscordCta variant="banner" label="Made with Jammable">
            Join the community making covers like this
          </DiscordCta>
        </div>
      </div>
    </>
  );
}

function getCountryFromRequest(
  context: GetServerSidePropsContext,
): string | null {
  const cfCountry = context.req.headers["cf-ipcountry"] as string | undefined;
  if (cfCountry) return cfCountry;

  const vercelCountry = context.req.headers["x-vercel-ip-country"] as
    | string
    | undefined;
  if (vercelCountry) return vercelCountry;

  return null;
}

export async function getServerSideProps(
  context: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<ConversionPageProps>> {
  const ConversionModel = (await import("@/models/Conversion")).default;
  const country = getCountryFromRequest(context);
  const isUK = country === "GB";
  // const conversion = await getConversion(
  //   context.params?.conversionid as string,
  // );

  await connectToDb();
  const conversion = await ConversionModel.aggregate([
    { $match: { shortid: context.params?.conversionid as string } },
    {
      $lookup: {
        from: "models",
        localField: "modelIds",
        foreignField: "_id",
        as: "models",
      },
    },
    {
      $lookup: {
        from: "users",
        localField: "userid",
        foreignField: "firebaseUid",
        as: "user",
      },
    },
    { $unwind: { path: "$user", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "models",
        localField: "modelid",
        foreignField: "_id",
        as: "model",
      },
    },
    { $unwind: { path: "$model", preserveNullAndEmptyArrays: true } },
    {
      $lookup: {
        from: "audiofiles",
        localField: "inputFileIds",
        foreignField: "_id",
        as: "inputFiles",
      },
    },
    {
      $project: {
        status: 1,
        unlockedTo: 1,
        shortid: 1,
        name: 1,
        public: 1,
        image: 1,
        // Only what the byline shows; see api/model/getConversion.js.
        user: {
          avatar: 1,
          username: 1,
          _id: 1,
          verifiedAccount: 1,
        },
        model: {
          image: 1,
          artistname: 1,
          _id: 1,
          shortid: 1,
          urlParam: 1,
        },
        models: {
          image: 1,
          artistname: 1,
          _id: 1,
          shortid: 1,
          urlParam: 1,
        },
        downloads: 1,
        createdAt: 1,
        conversionType: 1,
        info: 1,
        outputUrls: 1,
        convertedUrl: 1,
        instrumentalUrl: 1,
        removal: 1,
        deleted: 1,
        toolMode: 1,
        librarySongId: 1,
        duration: 1,
        clip: 1,
        isFree: 1,
        progress: 1,
        stage: 1,
        modelIds: 1,
        voiceTimes: 1,
        inputFileIds: 1,
        inputFiles: { _id: 1, metadata: 1 },
        videoSettings: 1,
      },
    },
  ]);

  if (!conversion[0] || conversion[0].removal) {
    return notFoundPage(context);
  }
  // A locked cover that has been unlocked lives on as the cover it became.
  if (conversion[0].status === "unlocked" && conversion[0].unlockedTo) {
    return {
      redirect: {
        destination: localePath(
          context.locale,
          `/conversion/${conversion[0].unlockedTo}`,
        ),
        permanent: false,
      },
    };
  }
  conversion[0].models = orderModelsByIds(
    conversion[0].models,
    conversion[0].modelIds,
  );
  let serializableConversion = JSON.parse(
    JSON.stringify(conversion[0], (key, value) =>
      key === "_id" ? value.toString() : value,
    ),
  );
  serializableConversion = JSON.parse(
    JSON.stringify(serializableConversion, (key, value) =>
      value === undefined ? null : value,
    ),
  );

  serializableConversion = {
    ...serializableConversion,
  };

  return {
    props: {
      conversion: serializableConversion,
      isUK,
    },
  };
}
